import { api, camelToSnake , apiFetch } from '@/core/api/client'
import type { Course, CoursePricing } from '@/features/classes/stores/types'

const coursesCollection = api.collection<Course>('courses')

export interface CourseSchedule {
  id: string
  course: string
  courseSlug: string
  courseName: string
  dayOfWeek: number
  dayName: string
  startTime: string
  endTime?: string
  locationId?: string
  maxAttendees: number
  isActive: boolean
  weeksAhead: number
  language: string
  skipDates?: string[]
  generatedUntil?: string
  instructorId?: string
  createdAt?: string
  updatedAt?: string
}

export async function fetchCourses(): Promise<Course[]> {
  return coursesCollection.getFullList({ sort: 'order' })
}

export async function fetchCourseBySlug(slug: string): Promise<Course | null> {
  try {
    return await coursesCollection.getOne(slug)
  } catch {
    return null
  }
}

export async function createCourse(data: Omit<Course, 'id' | 'createdAt' | 'updatedAt'>): Promise<Course> {
  return coursesCollection.create(camelToSnake(data) as Record<string, unknown>)
}

export async function updateCourse(slug: string, data: Partial<Course>): Promise<Course> {
  return coursesCollection.update(slug, camelToSnake(data) as Record<string, unknown>)
}

export async function deleteCourse(slug: string): Promise<void> {
  await coursesCollection.delete(slug)
}

export async function fetchPricingsByCourse(courseSlug: string): Promise<CoursePricing[]> {
  const course = await fetchCourseBySlug(courseSlug)
  return course?.pricings?.filter((p) => (p as CoursePricing & { isActive?: boolean }).isActive !== false) || []
}

export async function fetchAllPricings(): Promise<CoursePricing[]> {
  const courses = await fetchCourses()
  return courses.flatMap((c) => c.pricings || [])
}

export async function fetchB2bPricing(courseSlug: string): Promise<CoursePricing | null> {
  const pricings = await fetchPricingsByCourse(courseSlug)
  return pricings.find((p) => p.priceType === 'b2b') || null
}

export async function fetchB2cPricing(courseSlug: string): Promise<CoursePricing | null> {
  const pricings = await fetchPricingsByCourse(courseSlug)
  return pricings.find((p) => p.priceType === 'b2c') || null
}

// Course Schedules
const schedulesCollection = api.collection<CourseSchedule>('course-schedules')

export async function fetchCourseSchedules(): Promise<CourseSchedule[]> {
  return schedulesCollection.getFullList({ sort: 'course' })
}

export async function createCourseSchedule(data: {
  course: string
  dayOfWeek: number
  startTime: string
  endTime?: string
  locationId?: string
  maxAttendees: number
  weeksAhead?: number
  language?: string
  instructorId?: string
  isActive?: boolean
}): Promise<CourseSchedule> {
  return schedulesCollection.create(camelToSnake(data) as Record<string, unknown>)
}

export async function updateCourseSchedule(id: string, data: Partial<CourseSchedule>): Promise<CourseSchedule> {
  return schedulesCollection.update(id, camelToSnake(data) as Record<string, unknown>)
}

export async function deleteCourseSchedule(id: string): Promise<void> {
  await schedulesCollection.delete(id)
}


export async function regenerateCourseSchedule(id: string, weeks?: number): Promise<{ updated: number; created: number }> {
  const res = await apiFetch(`/course-schedules/${id}/regenerate/`, {
    method: 'POST',
    body: JSON.stringify({ weeks: weeks ?? undefined }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || 'Failed to regenerate sessions')
  }
  return res.json()
}

export async function generateSessions(weeks?: number): Promise<{ created: number; weeks: number }> {
  const res = await apiFetch('/course-schedules/generate/', {
    method: 'POST',
    body: JSON.stringify({ weeks: weeks ?? 4 }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || 'Failed to generate sessions')
  }
  return res.json()
}

export async function generateSessionsForCourse(courseSlug: string, weeks?: number): Promise<{ created: number; course: string; weeks: number }> {
  const res = await apiFetch('/course-schedules/generate_course/', {
    method: 'POST',
    body: JSON.stringify({ course_slug: courseSlug, weeks: weeks ?? 4 }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || 'Failed to generate sessions')
  }
  return res.json()
}
